// Path matcher.
//
// Pure functions, no Svelte. Tested via unit tests (see ./match.test.ts
// once we wire up vitest in a later phase). The contract:
//
//   matchRoutes(routes, "/agents/123/detail")
//     → [ {route: <agents layout>, params: {}},
//         {route: <:id child>,     params: {id: "123"}},
//         {route: <detail child>,  params: {id: "123"}} ]
//
// `null` return means no match.

import type { Route, RouteMatch } from "./types";

/** Split a URL path into segments, stripping leading/trailing slashes
 *  and discarding empty segments from `//` runs. */
function toSegments(path: string): string[] {
  return path.split("/").filter((s) => s.length > 0);
}

/** Top-level entry point. Walks the tree looking for a chain that
 *  consumes the entire pathname. Returns the chain from root to leaf,
 *  or null if no route matches. */
export function matchRoutes(
  routes: Route[],
  pathname: string,
): RouteMatch[] | null {
  return walk(routes, toSegments(pathname), {});
}

/** Recursive walk: try each route at this level; on a match, recurse
 *  into children with the remaining segments. */
function walk(
  routes: Route[],
  remaining: string[],
  paramsSoFar: Record<string, string>,
): RouteMatch[] | null {
  for (const route of routes) {
    const result = matchSegments(route.path, remaining, paramsSoFar);
    if (result === null) continue;

    const { params, rest, wildcard } = result;
    const match: RouteMatch = { route, params };

    if (wildcard) {
      // Catch-all routes consume everything regardless of children.
      return [match];
    }

    const hasChildren = route.children && route.children.length > 0;
    if (!hasChildren) {
      // Leaf: must have consumed every segment.
      if (rest.length === 0) return [match];
      continue;
    }

    // Layout: recurse into children. The parent matched but children
    // failing to match means this branch is wrong — keep trying
    // siblings at this level.
    const childChain = walk(route.children!, rest, params);
    if (childChain !== null) {
      return [match, ...childChain];
    }
  }
  return null;
}

/** Try to consume `route.path`'s segments against the front of `remaining`.
 *  Returns the new params accumulator + remaining segments, or null. */
function matchSegments(
  routePath: string,
  remaining: string[],
  paramsSoFar: Record<string, string>,
): { params: Record<string, string>; rest: string[]; wildcard: boolean } | null {
  const routeSegs = toSegments(routePath);

  // Empty path matches zero segments — useful for default-child
  // routes that share the parent's full path.
  if (routeSegs.length === 0) {
    return { params: paramsSoFar, rest: remaining, wildcard: false };
  }

  if (routeSegs.length > remaining.length) return null;

  const params = { ...paramsSoFar };
  for (let i = 0; i < routeSegs.length; i++) {
    const rs = routeSegs[i];
    const ps = remaining[i];

    if (rs === "*") {
      // Catch-all. Must be the LAST segment of the route path
      // (anything after it would be unreachable).
      return { params, rest: [], wildcard: true };
    }

    if (rs.startsWith(":")) {
      // Named param.
      const name = rs.slice(1);
      // decodeURIComponent so users land with usable strings — the
      // browser percent-encodes anything spicy in the pathname.
      params[name] = safeDecode(ps);
      continue;
    }

    // Literal segment.
    if (rs !== ps) return null;
  }

  return {
    params,
    rest: remaining.slice(routeSegs.length),
    wildcard: false,
  };
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    // Malformed percent escape — fall back to the raw value rather
    // than crashing the router.
    return s;
  }
}
